import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../ui/command';
import { useSceneShots } from '../../hooks/queries/useSceneShots';
import { buildShotSwitchUrl, extractDeepIds } from './urlHelpers';
import { cn } from '../../lib/utils';

/**
 * ShotPicker — the [Shot N ▾] chip that only renders when the URL
 * contains a shot id segment. Mirrors ScenePicker logic.
 *
 * Note: at time of Phase 2 implementation there is no shot-detail route
 * in App.jsx, so this chip won't render in practice today. Code is
 * preserved per spec to light up automatically when such a route exists.
 */
export default function ShotPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const { sceneId, shotId } = useMemo(
    () => extractDeepIds(location.pathname),
    [location.pathname]
  );

  const shotsQuery = useSceneShots(sceneId, { enabled: Boolean(sceneId && shotId) });
  const shots = shotsQuery.data || [];

  if (!shotId) return null;

  const current = shots.find((s) => String(s.id) === String(shotId));
  const shotNumber = current?.shot_number ?? current?.number ?? null;
  const shotTitle = current?.title || current?.name || '';
  const label = current
    ? shotNumber != null
      ? `Shot ${shotNumber}${shotTitle ? ' · ' + shotTitle : ''}`
      : shotTitle || 'Shot'
    : 'Shot';

  const handleSelect = (id) => {
    setOpen(false);
    if (!id || String(id) === String(shotId)) return;
    const url = buildShotSwitchUrl(location.pathname, id);
    if (url) navigate(url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select shot"
          data-testid="ctx-switcher-shot-trigger"
          data-state={open ? 'open' : 'closed'}
          className={cn(
            'group inline-flex items-center gap-1.5 max-w-[180px] md:max-w-[220px]',
            'h-7 px-2 rounded-md text-xs font-medium',
            'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            'data-[state=open]:bg-muted data-[state=open]:text-foreground',
            'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 opacity-60 transition-transform',
              'group-data-[state=open]:rotate-180'
            )}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-0"
        data-testid="ctx-switcher-shot-popover"
      >
        <Command>
          <CommandInput placeholder="Search shots…" aria-label="Search shots" />
          <CommandList>
            <CommandEmpty>No shots.</CommandEmpty>
            {shotsQuery.isLoading && (
              <div className="py-6 text-center text-xs text-muted-foreground">Loading shots…</div>
            )}
            {shots.length > 0 && (
              <CommandGroup heading="Shots">
                {shots.map((s) => {
                  const n = s.shot_number ?? s.number ?? '';
                  const t = s.title || s.name || '';
                  return (
                    <CommandItem
                      key={s.id}
                      value={`${n} ${t} ${s.id}`}
                      onSelect={() => handleSelect(s.id)}
                      data-testid={`ctx-switcher-shot-option-${s.id}`}
                    >
                      <span className="text-xs text-muted-foreground w-10 shrink-0">
                        {n !== '' ? `#${n}` : ''}
                      </span>
                      <span className="truncate flex-1">{t || 'Untitled'}</span>
                      {String(s.id) === String(shotId) && (
                        <Check className="h-4 w-4 text-primary" aria-label="current" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
